/**
 * adminPathMapping.js
 *
 * Ordered admin-path -> mobile-screen mapping table for Universal/App Links
 * opened from `admin.happysolutions.hu`. Pure data + a pure matcher — no
 * React / React Native / React Navigation imports on purpose, so this file
 * (and `adminLinkResolver.js`, which is built on top of it) can be unit
 * tested with plain Jest and no native-module mocking.
 *
 * Keep this table in sync with TWO other places whenever a route is added,
 * renamed or a new mobile screen is built:
 *   1. `admin/public/.well-known/apple-app-site-association` (the admin repo)
 *      — iOS will only hand a path to this app if it's ALSO listed there.
 *   2. `admin/src/config/pages/admin.js` — the source of truth for which
 *      admin paths actually exist.
 *
 * Matching rules (see `matchOne` below):
 *   - A pattern segment starting with `:` captures that path segment as a
 *     named param (e.g. `/crm/leads/:id` captures `id`).
 *   - A pattern ending in `*` (with or without a preceding `/`) is a PREFIX
 *     match: it matches that exact path AND everything nested under it.
 *   - Every other pattern must match the path segment-for-segment, same
 *     length, exactly.
 *   - Entries are tried TOP TO BOTTOM; the first match wins. This mirrors
 *     the task's ordered mapping table — order matters where two patterns
 *     could otherwise both apply.
 *   - A path that matches nothing is the caller's responsibility to default
 *     (see `adminLinkResolver.js`, which opens it in `AdminWebScreen` — the
 *     signed-in admin page inside the app — so every admin URL gets its own
 *     page even without a native screen).
 */

import ADMIN_MODULES from '../../config/adminModules';

// Each entry: { pattern, screen, buildParams(matchedParams, query), anyMode, when(query) }.
// `buildParams` is omitted for screens that take no route params; its second
// argument is the URL's decoded query (`{message: '…'}`), for the admin links
// that carry the record id there (`/mail/inbox?message=<id>`, see admin's
// `src/components/common/entityLinks.js`). `when` makes an entry apply only
// when the query predicate holds (e.g. a detail variant of a list route);
// such entries precede their query-less sibling. `anyMode` marks screens
// registered in the root stack of every app mode (no management gate /
// switch; the screen does its own access check). `screen: null` = a known
// admin path the app must NOT open (stays on web).
const BASE_PATH_TABLE = [
    // --- Web-only: payment / OAuth returns and the password reset consume a one-time
    //     state on load, so the app must never swallow them (excluded in the
    //     admin AASA too). Listed first so no catch-all below can claim them. ---
    {pattern: '/billing/success', screen: null},
    {pattern: '/billing/failed', screen: null},
    {pattern: '/integrations/viva-connect-callback', screen: null},
    {pattern: '/reset-password', screen: null},
    // Passwordless sign-in link (single-use `?token=`): the admin web consumes it,
    // like iOS (AASA excludes every `?token=` URL). In the app the user types the
    // 6-digit code from the same e-mail instead (MagicLinkLoginModal).
    {pattern: '/auth/magic-link', screen: null},

    // --- Sign-in pages: the app is already signed in -> its home. ---------
    {pattern: '/auth/*', screen: 'ManagementHomeScreen'},

    // --- Dashboards ----------------------------------------------------------
    {pattern: '/', screen: 'ManagementHomeScreen'},
    {pattern: '/main/dashboard', screen: 'ManagementHomeScreen'},
    {pattern: '/dashboard/*', screen: 'ManagementHomeScreen'},

    // --- CRM ---------------------------------------------------------------
    {pattern: '/crm', screen: 'ManagementCrmScreen'},
    {pattern: '/crm/main', screen: 'ManagementCrmScreen'},
    {pattern: '/leads', screen: 'ManagementListScreen', buildParams: () => ({type: 'leads'})},
    {pattern: '/crm/leads', screen: 'ManagementListScreen', buildParams: () => ({type: 'leads'})},
    // Static `/crm/leads/<page>` routes MUST precede `/crm/leads/:id`, or the
    // page name would be opened as a lead id.
    {pattern: '/crm/leads/apollo', screen: 'ApolloSearchScreen'},
    {pattern: '/crm/leads/import', screen: 'ManagementListScreen', buildParams: () => ({type: 'leads'})},
    {pattern: '/crm/leads/explee', screen: 'ManagementListScreen', buildParams: () => ({type: 'leads'})},
    {pattern: '/crm/leads/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'leads', id: m.id})},
    {pattern: '/crm/pipeline', screen: 'ManagementListScreen', buildParams: () => ({type: 'leads'})},
    // Sales follow-ups ("Teendők"); `/crm/leads/:id?followUp=<id>` opens the lead.
    {pattern: '/crm/follow-ups', screen: 'SalesFollowUpsScreen'},
    {pattern: '/crm/customers', screen: 'ManagementListScreen', buildParams: () => ({type: 'customers'})},
    {pattern: '/crm/customers/new', screen: 'ManagementListScreen', buildParams: () => ({type: 'customers'})},
    {pattern: '/crm/customers/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'customers', id: m.id})},
    {pattern: '/crm/store-customers', screen: 'ManagementListScreen', buildParams: () => ({type: 'unifiedCustomers'})},
    // Unified customer ids span every source collection — only the
    // `unifiedCustomers` type resolves them (GET /customers/unified/{id}).
    {pattern: '/customers/unified', screen: 'ManagementListScreen', buildParams: () => ({type: 'unifiedCustomers'})},
    {pattern: '/customers/unified/:customerId', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'unifiedCustomers', id: m.customerId})},
    {pattern: '/sales/customers', screen: 'ManagementListScreen', buildParams: () => ({type: 'unifiedCustomers'})},
    {pattern: '/sales/customers/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'unifiedCustomers', id: m.id})},

    // --- Calendar: a ManagementHomeScreen tab, opened via nested params (the
    //     same route SalesFollowUpsScreen uses). Admin links one event as
    //     `/calendar/main?event=<id>[&date=<ISO>]`; CalendarScreen jumps to the
    //     date and opens the event once (`focusEventId` / `focusDate`). ---
    {pattern: '/calendar/settings', screen: 'CalendarSettingsScreen'},
    {
        pattern: '/calendar/*',
        when: (q) => !!q.event,
        screen: 'ManagementHomeScreen',
        buildParams: (m, q) => ({
            screen: 'ManagementCalendar',
            params: {focusEventId: q.event, ...(q.date ? {focusDate: q.date} : {})},
        }),
    },
    {pattern: '/calendar/*', screen: 'ManagementHomeScreen', buildParams: () => ({screen: 'ManagementCalendar'})},

    // --- Service -------------------------------------------------------------
    {pattern: '/service', screen: 'ManagementServiceScreen'},
    {pattern: '/service/dashboard', screen: 'ManagementServiceScreen'},
    {pattern: '/service/work-orders', screen: 'ManagementListScreen', buildParams: () => ({type: 'workOrders'})},
    {pattern: '/service/tickets', screen: 'ManagementListScreen', buildParams: () => ({type: 'workOrders'})},
    {pattern: '/service/tickets/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'workOrders', id: m.id})},
    {pattern: '/service/customers', screen: 'ManagementListScreen', buildParams: () => ({type: 'customers'})},
    {pattern: '/service/customers/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'customers', id: m.id})},
    {pattern: '/service/quotes', screen: 'ManagementListScreen', buildParams: () => ({type: 'quotes'})},
    {pattern: '/service/quotes/new', screen: 'ManagementQuoteEditorScreen'},
    {pattern: '/service/quotes/edit/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'quotes', id: m.id})},
    {pattern: '/service/modes', screen: 'ManagementServiceModesScreen'},
    {pattern: '/service/*', screen: 'ManagementServiceScreen'},

    // --- Invoices / NAV --------------------------------------------------
    {pattern: '/nav/incoming-invoices', screen: 'ManagementListScreen', buildParams: () => ({type: 'invoicesInbound'})},
    {pattern: '/invoices/main', screen: 'ManagementListScreen', buildParams: () => ({type: 'invoicesInbound'})},
    {pattern: '/nav/sent-invoices', screen: 'ManagementListScreen', buildParams: () => ({type: 'invoicesOutbound'})},
    {pattern: '/nav/invoice/new', screen: 'ManagementInvoiceEditorScreen', buildParams: () => ({direction: 'OUTBOUND'})},
    {pattern: '/nav/invoice-details/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'invoicesInbound', id: m.id})},
    {pattern: '/invoices/:invoiceId', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'invoicesInbound', id: m.invoiceId})},
    {pattern: '/nav', screen: 'ManagementListScreen', buildParams: () => ({type: 'invoicesInbound'})},
    {pattern: '/nav/main', screen: 'ManagementListScreen', buildParams: () => ({type: 'invoicesInbound'})},

    // --- Finance -------------------------------------------------------------
    {pattern: '/finance/*', screen: 'ManagementFinanceScreen'},
    {pattern: '/billing/*', screen: 'ManagementBillingScreen'},
    {pattern: '/data/banks', screen: 'ManagementListScreen', buildParams: () => ({type: 'banks'})},
    {pattern: '/data/cost-types', screen: 'ManagementListScreen', buildParams: () => ({type: 'costTypes'})},

    // --- Quotes --------------------------------------------------------------
    {pattern: '/quotes', screen: 'ManagementListScreen', buildParams: () => ({type: 'quotes'})},
    {pattern: '/quotes/main', screen: 'ManagementListScreen', buildParams: () => ({type: 'quotes'})},
    {pattern: '/quotes/new', screen: 'ManagementQuoteEditorScreen'},
    {pattern: '/quotes/master-data', screen: 'ManagementListScreen', buildParams: () => ({type: 'quotes'})},
    {pattern: '/crm/quotes', screen: 'ManagementListScreen', buildParams: () => ({type: 'quotes'})},
    {pattern: '/crm/quotes/new', screen: 'ManagementQuoteEditorScreen'},
    {pattern: '/crm/quotes/master-data', screen: 'ManagementListScreen', buildParams: () => ({type: 'quotes'})},
    {pattern: '/crm/quotes/templates', screen: 'ManagementListScreen', buildParams: () => ({type: 'quotes'})},
    {pattern: '/crm/quotes/edit/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'quotes', id: m.id})},
    {pattern: '/quotes/edit/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'quotes', id: m.id})},

    // --- Orders / products ---------------------------------------------------
    {pattern: '/order/tracking', when: (q) => !!q.orderId, screen: 'ManagementDetailScreen', buildParams: (m, q) => ({type: 'orders', id: q.orderId})},
    {pattern: '/order/tracking', screen: 'ManagementListScreen', buildParams: () => ({type: 'orders'})},
    {pattern: '/pos/sales', screen: 'ManagementListScreen', buildParams: () => ({type: 'orders'})},
    {pattern: '/data/food', when: (q) => !!q.foodId, screen: 'ManagementDetailScreen', buildParams: (m, q) => ({type: 'products', id: q.foodId})},
    {pattern: '/data/food', screen: 'ManagementListScreen', buildParams: () => ({type: 'products'})},
    {pattern: '/data/warehouses', screen: 'ManagementListScreen', buildParams: () => ({type: 'warehouses'})},

    // --- Procurement (root stack, every mode — requesters are not managers).
    //     MUST precede the `/stock/*` catch-all. ---
    {pattern: '/stock/procurement', screen: 'ProcurementHomeScreen', anyMode: true},
    {pattern: '/stock/procurement/requisitions', screen: 'ProcurementRequisitionsScreen', anyMode: true},
    {pattern: '/stock/procurement/requisitions/:id', screen: 'ProcurementRequisitionDetailScreen', anyMode: true, buildParams: (m) => ({id: m.id})},
    {pattern: '/stock/procurement/rfqs', screen: 'ProcurementRfqsScreen', anyMode: true},
    {pattern: '/stock/procurement/rfqs/:id', screen: 'ProcurementRfqDetailScreen', anyMode: true, buildParams: (m) => ({id: m.id})},
    {pattern: '/stock/procurement/orders', screen: 'ProcurementOrdersScreen', anyMode: true},
    {pattern: '/stock/procurement/orders/:id', screen: 'ProcurementOrderDetailScreen', anyMode: true, buildParams: (m) => ({id: m.id})},
    {pattern: '/stock/procurement/sourcing', screen: 'ProcurementSourcingScreen', anyMode: true},
    {pattern: '/stock/procurement/reorder-rules', screen: 'ProcurementReorderRulesScreen', anyMode: true},
    {pattern: '/stock/procurement/settings', screen: 'ProcurementSettingsScreen', anyMode: true},
    {pattern: '/stock/procurement/*', screen: 'ProcurementHomeScreen', anyMode: true},

    // --- Stock / inventory -----------------------------------------------
    {pattern: '/stock/transactions', screen: 'ManagementListScreen', buildParams: () => ({type: 'transactions'})},
    {pattern: '/stock/transactions/:transactionId', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'transactions', id: m.transactionId})},
    {pattern: '/stock/*', screen: 'ManagementListScreen', buildParams: () => ({type: 'stock'})},
    {pattern: '/inventory', screen: 'ManagementListScreen', buildParams: () => ({type: 'stock'})},

    // --- Fleet / logistics / utilities -----------------------------------
    {pattern: '/fleet-management/main', screen: 'ManagementFleetStatsScreen'},
    {pattern: '/fleet-management/refuels', screen: 'ManagementRefuelsScreen'},
    {pattern: '/fleet-management/driver-breaks', screen: 'ManagementDriverBreaksScreen'},
    {pattern: '/fleet-management/*', screen: 'ManagementLogisticsScreen'},
    {pattern: '/fleet/*', screen: 'ManagementLogisticsScreen'},
    {pattern: '/logisztika/*', screen: 'ManagementLogisticsScreen'},
    {pattern: '/utilities', screen: 'UtilityHomeScreen'},

    // --- Automations -----------------------------------------------------
    {pattern: '/automations', screen: 'ManagementAutomationsScreen'},
    {pattern: '/automations/main', screen: 'ManagementAutomationsScreen'},

    // --- Partners — no dedicated mobile screen; Customers is the nearest match ---
    {pattern: '/data/partner', screen: 'ManagementListScreen', buildParams: () => ({type: 'customers'})},
    {pattern: '/partners', screen: 'ManagementListScreen', buildParams: () => ({type: 'customers'})},
    {pattern: '/partners/main', screen: 'ManagementListScreen', buildParams: () => ({type: 'customers'})},

    // --- Store administration --------------------------------------------
    {pattern: '/settings/store', screen: 'ManagementStoreSettingsScreen'},
    {pattern: '/settings/general', screen: 'ManagementStoreSettingsScreen'},
    {pattern: '/settings/employees', screen: 'ManagementUsersScreen'},
    {pattern: '/settings/role-templates', screen: 'ManagementRolesScreen'},
    {pattern: '/settings/module-templates', screen: 'ManagementModuleTemplatesScreen'},
    {pattern: '/settings/integrations', screen: 'ManagementListScreen', buildParams: () => ({type: 'integrations'})},
    {pattern: '/integrations/main', screen: 'ManagementListScreen', buildParams: () => ({type: 'integrations'})},
    {pattern: '/settings/developer/cicd', screen: 'ManagementDeploymentsScreen'},
    {pattern: '/ai/insights', screen: 'HappyAiInsightsScreen'},

    // --- Mail (root stack, every mode). Admin links a single message as
    //     `/mail/inbox?message=<id>` (entityLinks.js / notificationUtils.js). ---
    {pattern: '/mail/*', when: (q) => !!q.message, screen: 'MailThreadScreen', anyMode: true, buildParams: (m, q) => ({messageId: q.message})},
    {pattern: '/mail/compose', screen: 'MailComposeScreen', anyMode: true},
    {pattern: '/mail/sent', screen: 'MailHomeScreen', anyMode: true, buildParams: () => ({folder: 'Sent'})},
    {pattern: '/mail/drafts', screen: 'MailHomeScreen', anyMode: true, buildParams: () => ({folder: 'Drafts'})},
    {pattern: '/mail/scheduled', screen: 'MailScheduledScreen', anyMode: true},
    {pattern: '/mail/settings', screen: 'MailSettingsScreen', anyMode: true},
    {pattern: '/mail/templates', screen: 'MailTemplatesScreen', anyMode: true},
    {pattern: '/mail/automations', screen: 'MailRulesScreen', anyMode: true},
    {pattern: '/mail/*', screen: 'MailHomeScreen', anyMode: true},

    // --- Personal tools (root stack, every mode) ---------------------------
    {pattern: '/system/notifications', screen: 'NotificationsScreen', anyMode: true},
    {pattern: '/employee/time-entry', screen: 'TimeTrackerScreen', anyMode: true},
    {pattern: '/user/settings', screen: 'SettingsScreen', anyMode: true},
    {pattern: '/ai/main', screen: 'AIAssistantScreen', anyMode: true},
    {pattern: '/ai/chat', screen: 'AIAssistantScreen', anyMode: true},
    {pattern: '/settings/ai-agent', screen: 'AiAgentSettingsScreen', anyMode: true},
    {pattern: '/voip/calls', screen: 'CallHistoryScreen', anyMode: true},
    {pattern: '/voip/directory', screen: 'DirectoryScreen', anyMode: true},
    {pattern: '/phone/settings', screen: 'VoipSettingsScreen', anyMode: true},
    {pattern: '/settings/meeting-transcripts', screen: 'MeetingRecordingSettingsScreen', anyMode: true},
    {pattern: '/meetings', screen: 'MeetingRecorderScreen', anyMode: true},
    {pattern: '/meetings/:id', screen: 'MeetingDetailScreen', anyMode: true, buildParams: (m) => ({id: m.id})},

    // --- Team Chat + meetings (module `csapatchat`). `anyMode`: these screens
    //     live in the root stack of EVERY app mode, so the link opens them
    //     without switching to (or requiring) management mode. `/chat/settings`
    //     MUST precede `/chat/:channelId` (same rule as admin's pages config).
    //     ChatMeetingScreen always opens on its native pre-join screen, so a
    //     link never joins (or spends credits) without a tap. `/meet/join` and
    //     `/meet/p/:code` are standalone web pages (in-app WebView room / public
    //     guest page) — deliberately NOT mapped. A chat notification links
    //     `/chat/<channelId>?message=<id>` -> jump to that message. ---
    {pattern: '/chat', screen: 'ChatHomeScreen', anyMode: true},
    {pattern: '/chat/settings', screen: 'ChatHomeScreen', anyMode: true},
    {pattern: '/chat/apps', screen: 'ChatHomeScreen', anyMode: true},
    {pattern: '/chat/apps/:appId', screen: 'ChatHomeScreen', anyMode: true},
    {
        pattern: '/chat/:channelId',
        screen: 'ChatChannelScreen',
        anyMode: true,
        buildParams: (m, q) => ({channelId: m.channelId, ...(q.message ? {messageId: q.message} : {})}),
    },
    {pattern: '/meet/join', screen: null},
    {pattern: '/meet/p/:code', screen: null},
    {pattern: '/meet/:meetingId', screen: 'ChatMeetingScreen', anyMode: true, buildParams: (m) => ({meetingId: m.meetingId})},

    // --- Projects + tasks (ManagementList/DetailScreen types `projects` /
    //     `tasks`). Static `/projects/<page>` routes MUST precede
    //     `/projects/:id`. The board and workspace have no native equivalent:
    //     the board opens its project, the workspace the project list. ---
    {pattern: '/projects', screen: 'ManagementListScreen', buildParams: () => ({type: 'projects'})},
    {pattern: '/projects/main', screen: 'ManagementListScreen', buildParams: () => ({type: 'projects'})},
    {pattern: '/projects/workspace', screen: 'ManagementListScreen', buildParams: () => ({type: 'projects'})},
    {pattern: '/projects/tasks', screen: 'ManagementListScreen', buildParams: () => ({type: 'tasks'})},
    {pattern: '/projects/tasks/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'tasks', id: m.id})},
    {pattern: '/project/tasks/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'tasks', id: m.id})},
    {pattern: '/projects/board/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'projects', id: m.id})},
    {pattern: '/projects/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'projects', id: m.id})},
    {pattern: '/tasks', screen: 'ManagementListScreen', buildParams: () => ({type: 'tasks'})},
    {pattern: '/tasks/*', screen: 'ManagementListScreen', buildParams: () => ({type: 'tasks'})},

    // --- Customer support (types `complaints` / `supportTickets`) ----------
    {pattern: '/customer-support/complaints/details/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'complaints', id: m.id})},
    {pattern: '/customer-support/complaints/*', screen: 'ManagementListScreen', buildParams: () => ({type: 'complaints'})},
    {pattern: '/customer-support/tickets', screen: 'ManagementListScreen', buildParams: () => ({type: 'supportTickets'})},
    {pattern: '/customer-support/tickets/:id', screen: 'ManagementDetailScreen', buildParams: (m) => ({type: 'supportTickets', id: m.id})},
];

const normalizePath = (path) => {
    if (!path) return '/';
    let p = String(path).split('?')[0].split('#')[0];
    if (!p.startsWith('/')) p = `/${p}`;
    if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
    return p || '/';
};

/**
 * Match a single pattern against an already-normalized path.
 * @returns {Record<string,string>|null} captured params (possibly empty), or null if no match.
 */
const matchOne = (pattern, path) => {
    if (pattern.endsWith('*')) {
        // '/finance/*' -> prefix '/finance'; '/projects*' -> prefix '/projects'.
        // Either shape matches the bare prefix itself AND anything nested under it.
        const prefix = pattern.slice(0, -1).replace(/\/$/, '');
        if (path === prefix || path.startsWith(`${prefix}/`)) return {};
        return null;
    }

    const patternSegs = pattern.split('/').filter(Boolean);
    const pathSegs = path.split('/').filter(Boolean);
    if (patternSegs.length !== pathSegs.length) return null;

    const params = {};
    for (let i = 0; i < patternSegs.length; i += 1) {
        const ps = patternSegs[i];
        const vs = pathSegs[i];
        if (ps.startsWith(':')) {
            if (!vs) return null;
            try {
                params[ps.slice(1)] = decodeURIComponent(vs);
            } catch (_error) {
                params[ps.slice(1)] = vs;
            }
        } else if (ps !== vs) {
            return null;
        }
    }
    return params;
};

/** Origin the catalog's web-fallback pages are opened on (AdminWebScreen only accepts admin-host URLs). */
const ADMIN_WEB_ORIGIN = 'https://admin.happysolutions.hu';

/**
 * Deep-link entries derived from the admin-module catalog (`config/adminModules`),
 * so every admin menu page resolves to exactly the target the Modules hub opens —
 * a native screen / list type, or AdminWebScreen — instead of being swallowed by a
 * broader catch-all in the table above (`/finance/*`, `/stock/*`, `/mail/*`, …).
 * A page whose path the base table already maps to the same target is skipped, so
 * hand-tuned base entries (and their `anyMode` flag) win. Placed in FRONT of the
 * base table: the first match wins.
 * @returns {Array<object>} Table entries.
 */
const buildCatalogEntries = () => {
    const entries = [];
    const seen = new Set();

    for (const mod of ADMIN_MODULES) {
        for (const page of mod.pages || []) {
            const path = normalizePath(page.adminPath);

            if (!page.adminPath || path.includes('*') || seen.has(path)) continue;
            seen.add(path);

            // Dynamic detail routes (`/crm/wiki/:id`): `params` values written as ':id' are filled from the matched segment.
            const dynamic = path.includes(':');

            if (dynamic && !page.screen && !page.list) continue;

            let screen = 'AdminWebScreen';
            let params = {url: `${ADMIN_WEB_ORIGIN}${path}`};
            let anyMode = true;

            if (page.screen) {
                screen = page.screen;
                params = page.params;
                anyMode = false;
            } else if (page.list) {
                screen = 'ManagementListScreen';
                params = {type: page.list};
                anyMode = false;
            }

            const matching = BASE_PATH_TABLE.filter((entry) => matchOne(entry.pattern, path));
            const base = matching.find((entry) => !entry.when) || null;

            // Hand-tuned base entries win: web-only paths (payment returns, reset-password…) stay
            // untouched, a query-gated entry (`/mail/*?message=`, `/calendar/*?event=`) must not be
            // shadowed, and an exact (non catch-all) base mapping keeps its target and `anyMode`.
            if (matching.some((entry) => entry.when)) continue;
            if (base && (base.screen === null || (!base.pattern.endsWith('*') && base.screen !== 'AdminWebScreen'))) continue;

            const fillParams = (matched) => {
                if (!dynamic || !params) return params;

                return Object.keys(params).reduce((acc, key) => {
                    const value = params[key];

                    acc[key] = typeof value === 'string' && value.startsWith(':') ? matched[value.slice(1)] : value;

                    return acc;
                }, {});
            };

            entries.push({pattern: path, screen, buildParams: dynamic ? fillParams : () => params, ...(anyMode ? {anyMode: true} : {})});
        }
    }

    return entries;
};

const ADMIN_PATH_TABLE = [...buildCatalogEntries(), ...BASE_PATH_TABLE];

/**
 * Decode a URL's query string into a plain object (first value wins). Hand
 * rolled for the same reason as `parseUrl` in `adminLinkResolver.js`: no
 * reliable `URL`/`URLSearchParams` on Hermes without a polyfill.
 * @param {string} url a full URL or a path; only the part after `?` (and before `#`) is read.
 * @returns {Record<string,string>}
 */
export const parseQuery = (url) => {
    const query = {};
    const raw = String(url || '').split('#')[0];
    const start = raw.indexOf('?');
    if (start === -1) return query;
    for (const part of raw.slice(start + 1).split('&')) {
        if (!part) continue;
        const eq = part.indexOf('=');
        const rawKey = eq === -1 ? part : part.slice(0, eq);
        const rawValue = eq === -1 ? '' : part.slice(eq + 1);
        const decode = (value) => {
            try {
                return decodeURIComponent(value.replace(/\+/g, ' '));
            } catch (_error) {
                return value;
            }
        };
        const key = decode(rawKey);
        if (key && !Object.prototype.hasOwnProperty.call(query, key)) query[key] = decode(rawValue);
    }
    return query;
};

/**
 * Resolve an admin-host pathname to its mobile screen + params, per
 * `ADMIN_PATH_TABLE`.
 * @param {string} rawPath e.g. `/crm/leads/64f...` or `/mail/inbox?message=…` (hash is ignored).
 * @param {Record<string,string>} [query] decoded query; parsed from `rawPath` when omitted.
 * @returns {{screen: string|null, params: object|undefined, anyMode?: boolean}|null}
 *          null when unmapped; `screen: null` when deliberately web-only.
 */
export const matchAdminPath = (rawPath, query) => {
    const path = normalizePath(rawPath);
    const q = query || parseQuery(rawPath);
    for (const entry of ADMIN_PATH_TABLE) {
        if (entry.when && !entry.when(q)) continue;
        const params = matchOne(entry.pattern, path);
        if (params) {
            return {
                screen: entry.screen,
                params: entry.buildParams ? entry.buildParams(params, q) : undefined,
                ...(entry.anyMode ? {anyMode: true} : {}),
            };
        }
    }
    return null;
};

export {ADMIN_PATH_TABLE};
export default matchAdminPath;
